import { useDeferredValue, useRef, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useWorkspace } from '../workspace';
import { searchOpenVault } from '../openVault';
import { searchVaultFiles } from '../vaultSearch';
import { fileEntry } from '../fileTree';
import { fileVisualKind } from '../fileKinds';
import { useImagePreviewUrl } from '../useImagePreviewUrl';
import { FileGlyph } from './FileGlyph';
import styles from './VaultSearch.module.css';

export function VaultSearch({
  onOpenPath,
  variant = 'sidebar',
}: {
  onOpenPath?: (path: string) => void;
  variant?: 'sidebar' | 'mobile';
}) {
  const { workspace } = useWorkspace();
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const [remoteHits, setRemoteHits] = useState<Array<{ path?: string; snippet: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [previewPath, setPreviewPath] = useState<string>();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const localHits =
    workspace && deferred.trim() ? searchVaultFiles(workspace.snapshot.files, deferred) : [];
  const fileByPath = new Map(workspace?.snapshot.files.map((file) => [file.path, file]));
  const previewFile = previewPath ? fileByPath.get(previewPath) : undefined;
  const imagePreview = useImagePreviewUrl(
    previewFile && fileVisualKind(previewFile) === 'image' ? previewFile : undefined,
  );

  const runRemote = async (value: string) => {
    if (!workspace || workspace.vaultMode !== 'open' || !value.trim()) {
      setRemoteHits([]);
      return;
    }
    setSearching(true);
    try {
      const result = await searchOpenVault(workspace.repositoryId, value.trim());
      setRemoteHits(result.matches.map((match) => ({ snippet: match.snippet })));
    } catch {
      setRemoteHits([]);
    } finally {
      setSearching(false);
    }
  };

  if (!workspace || workspace.mode === 'shared') return null;

  const collapsedMobileSearch = variant === 'mobile' && !mobileSearchOpen;

  return (
    <div
      className={`${styles.root} ${variant === 'mobile' ? styles.mobile : ''} ${
        collapsedMobileSearch ? styles.mobileCollapsed : ''
      }`}
    >
      {collapsedMobileSearch ? (
        <button
          type="button"
          className={styles.searchButton}
          aria-label="Search files"
          onClick={() => {
            setMobileSearchOpen(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
        >
          <MagnifyingGlass size={18} aria-hidden />
        </button>
      ) : null}
      {!collapsedMobileSearch ? (
        <label className={styles.field}>
          <MagnifyingGlass className={styles.searchIcon} size={15} aria-hidden />
          <input
            className={styles.input}
            ref={inputRef}
            value={query}
            placeholder="Search files…"
            onChange={(event) => {
              const value = event.target.value;
              setQuery(value);
              void runRemote(value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && variant === 'mobile') {
                setQuery('');
                setRemoteHits([]);
                setMobileSearchOpen(false);
              }
            }}
          />
        </label>
      ) : null}
      {deferred.trim() ? (
        <ul className={styles.results}>
          <li className={styles.resultSummary}>
            {localHits.length ? `${localHits.length} matching files` : 'Search results'}
          </li>
          {localHits.map((hit) => (
            <li key={hit.path}>
              <button
                type="button"
                className={styles.result}
                onClick={() => {
                  onOpenPath?.(hit.path);
                  setQuery('');
                  setRemoteHits([]);
                  setMobileSearchOpen(false);
                }}
                onMouseEnter={() => setPreviewPath(hit.path)}
                onMouseLeave={() => setPreviewPath(undefined)}
                onFocus={() => setPreviewPath(hit.path)}
                onBlur={() => setPreviewPath(undefined)}
              >
                {fileByPath.get(hit.path) ? (
                  <FileGlyph
                    entry={fileEntry(fileByPath.get(hit.path)!)}
                    size="tree"
                    density="compact"
                    showPreview={false}
                  />
                ) : null}
                <span className={styles.resultCopy}>
                  <span className={styles.path}>{hit.path.split('/').at(-1)}</span>
                  <span className={styles.location}>{hit.path}</span>
                  {hit.snippet ? <span className={styles.snippet}>{hit.snippet}</span> : null}
                </span>
                {previewPath === hit.path && imagePreview.src ? (
                  <span className={styles.hoverPreview} aria-hidden>
                    <img src={imagePreview.src} alt="" />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
          {workspace.vaultMode === 'open' && searching ? (
            <li className={styles.meta}>Searching server…</li>
          ) : null}
          {workspace.vaultMode === 'open' &&
            remoteHits.map((hit, index) => (
              <li key={`remote-${index}`} className={styles.remoteResult}>
                <span className={styles.remoteLabel}>Server match</span>
                <span className={styles.snippet}>{hit.snippet}</span>
              </li>
            ))}
          {!localHits.length && !remoteHits.length && !searching ? (
            <li className={styles.meta}>No matches</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
